/**
 * Phase 8 (applicant-web slice) — local mirror of the case-state
 * vocabulary the admin-web defines in `apps/admin-web/src/case/
 * caseState.ts`.
 *
 * Locked product decision: 7 states in pipeline order. Submit click in
 * the applicant intake auto-transitions `submitted` -> `in_review`,
 * which starts a 72-hour SLA window before the printed packet ships.
 *
 * The applicant-web does not currently depend on admin-web, and we
 * do not want to introduce a dependency from applicant-web onto an
 * admin surface. A follow-up can extract this into a shared package
 * if a third surface needs the same vocabulary. Until then, both
 * sides keep the literals in lockstep — the test in
 * `tests/PostSubmitView.test.tsx` enforces the post-submit subset
 * structurally.
 */

export const CASE_STATES = [
  'paid',
  'filling',
  'submitted',
  'in_review',
  'docket_ready',
  'printing',
  'shipped',
] as const;

export type TCaseState = (typeof CASE_STATES)[number];

const CASE_STATE_SET: ReadonlySet<string> = new Set(CASE_STATES);

/** Type-guard for arbitrary input from Firestore. */
export function isCaseState(value: unknown): value is TCaseState {
  return typeof value === 'string' && CASE_STATE_SET.has(value);
}

/**
 * The five states that mean "the applicant has submitted; case team
 * is now driving". When the application doc reports any of these,
 * the applicant should see the post-submit status surface, not the
 * editable intake.
 *
 * `paid` and `filling` deliberately stay editable so the existing
 * intake renders for them.
 */
export const POST_SUBMIT_STATES = [
  'submitted',
  'in_review',
  'docket_ready',
  'printing',
  'shipped',
] as const;

export type TPostSubmitState = (typeof POST_SUBMIT_STATES)[number];

const POST_SUBMIT_SET: ReadonlySet<string> = new Set(POST_SUBMIT_STATES);

export function isPostSubmitState(state: TCaseState): state is TPostSubmitState {
  return POST_SUBMIT_SET.has(state);
}

const LABELS: Record<TCaseState, string> = {
  paid: 'Paid',
  filling: 'Filling',
  submitted: 'Submitted',
  in_review: 'In review',
  docket_ready: 'Docket ready',
  printing: 'Printing',
  shipped: 'Shipped',
};

export function caseStateLabel(state: TCaseState): string {
  return LABELS[state];
}

/**
 * Existing applicant-web applications have no caseState field. The
 * spec dictates default `'filling'` for legacy docs (they made it
 * past payment but haven't been submitted yet, so the intake should
 * render normally).
 */
export function defaultCaseStateForUnknown(value: unknown): TCaseState {
  return isCaseState(value) ? value : 'filling';
}
